<template>
  <div :class="embedded ? '' : 'p-4 bg-white shadow rounded mb-6'">
    <div class="relative flex flex-wrap items-center gap-3">
      <select
        v-model="mode"
        class="border rounded px-3 py-1.5"
      >
        <option value="week">
          Week
        </option>
        <option value="month">
          Month
        </option>
        <option value="quarter">
          Quarter
        </option>
        <option value="year">
          Year
        </option>
        <option value="range">
          Custom Range
        </option>
      </select>

      <button
        type="button"
        class="min-w-[230px] rounded border border-gray-300 bg-white px-3 py-1.5 text-left"
        @click="isCalendarOpen = !isCalendarOpen"
      >
        {{ rangeLabel }}
      </button>

      <button
        class="bg-[#0dabcb] hover:bg-[#3c9ca4] text-white px-4 py-1.5 rounded"
        @click="apply"
      >
        Apply
      </button>

      <div
        v-if="isCalendarOpen"
        class="insights-calendar-popover rounded border border-gray-200 bg-white p-3 shadow-lg"
      >
        <DatePicker
          v-model="calendarRange"
          is-range
          mode="date"
          :attributes="attrs"
        />
        <p class="mt-2 max-w-xs text-xs text-gray-500">
          {{ calendarHelpText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore'
import 'v-calendar/dist/style.css'
import { DatePicker } from 'v-calendar'

defineProps({
  embedded: {
    type: Boolean,
    default: false
  }
})

const mode = ref('week')
const today = new Date()
const isCalendarOpen = ref(false)
const skipNextCalendarWatch = ref(false)
const calendarRange = ref({ start: today, end: today })
const pendingRange = ref({ start: today, end: today })

const store = useEmployeeInsightsStore()

const attrs = computed(() => [
  {
    key: 'selectedRange',
    highlight: {
      start: { fillMode: 'solid', color: 'gray' },
      base: { fillMode: 'light', color: 'gray' },
      end: { fillMode: 'solid', color: 'gray' },
    },
    dates: pendingRange.value
  }
])

const calendarHelpText = computed(() => {
  if (mode.value === 'range') return 'Select a start and end date.'
  return 'Select any date in the period. The matching range will be selected.'
})

const rangeLabel = computed(() => {
  const { start, end } = pendingRange.value
  if (!start || !end) return 'Select date range'
  return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`
})

function startOfWeek(date) {
  const start = new Date(date)
  const day = start.getDay() || 7
  start.setDate(start.getDate() - day + 1)
  return start
}

function buildRangeFromSelection(selection) {
  const selectedDate = selection?.start || selection
  if (!selectedDate) return pendingRange.value

  const selected = new Date(selectedDate)

  if (mode.value === 'week') {
    const start = startOfWeek(selected)
    return { start, end: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6) }
  }

  if (mode.value === 'month') {
    return {
      start: new Date(selected.getFullYear(), selected.getMonth(), 1),
      end: new Date(selected.getFullYear(), selected.getMonth() + 1, 0)
    }
  }

  if (mode.value === 'quarter') {
    return {
      start: new Date(selected.getFullYear(), selected.getMonth(), 1),
      end: new Date(selected.getFullYear(), selected.getMonth() + 3, 0)
    }
  }

  if (mode.value === 'year') {
    return {
      start: new Date(selected.getFullYear(), 0, 1),
      end: new Date(selected.getFullYear(), 11, 31)
    }
  }

  return {
    start: selection?.start ? new Date(selection.start) : selected,
    end: selection?.end ? new Date(selection.end) : new Date(selection?.start || selected)
  }
}

function setPendingRange(range) {
  pendingRange.value = range
  skipNextCalendarWatch.value = true
  calendarRange.value = range
}

function formatDisplayDate(date) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

function localCalendarDateFromEpoch(epoch) {
  const date = new Date(epoch * 1000)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

function modeForRange(start, end) {
  const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  const dayDiff = Math.round((endDateOnly - startDateOnly) / 86400000)
  const isFullWeek = dayDiff === 6
  const isFullMonth =
    start.getDate() === 1 &&
    new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate() === end.getDate()
  const quarterEnd = new Date(start.getFullYear(), start.getMonth() + 3, 0)
  const isThreeMonthRange =
    start.getDate() === 1 &&
    end.getFullYear() === quarterEnd.getFullYear() &&
    end.getMonth() === quarterEnd.getMonth() &&
    end.getDate() === quarterEnd.getDate()
  const isFullYear =
    start.getMonth() === 0 &&
    start.getDate() === 1 &&
    end.getMonth() === 11 &&
    end.getDate() === 31 &&
    start.getFullYear() === end.getFullYear()

  if (isFullYear) return 'year'
  if (isThreeMonthRange) return 'quarter'
  if (isFullWeek) return 'week'
  if (isFullMonth) return 'month'
  return 'range'
}

function syncFromStoreRange() {
  if (!store.startDate || !store.endDate) return false

  const start = localCalendarDateFromEpoch(store.startDate)
  const end = localCalendarDateFromEpoch(store.endDate)
  mode.value = modeForRange(start, end)
  setPendingRange({ start, end })
  return true
}

function apply() {
  const { start, end } = pendingRange.value
  if (!start || !end) return

  const startEpoch = Math.floor(Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  ) / 1000)

  const endEpoch = Math.floor(Date.UTC(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
    23, 59, 59
  ) / 1000)

  store.updateDateRange(startEpoch, endEpoch)
  isCalendarOpen.value = false
}

onMounted(() => {
  if (syncFromStoreRange()) return
  setPendingRange(buildRangeFromSelection(today))
  apply()
})

watch(calendarRange, (newRange) => {
  if (skipNextCalendarWatch.value) {
    skipNextCalendarWatch.value = false
    return
  }
  if (!newRange) return
  setPendingRange(buildRangeFromSelection(newRange))
})

watch(mode, () => {
  setPendingRange(buildRangeFromSelection(pendingRange.value.start || today))
})

watch(
  () => [store.startDate, store.endDate],
  () => {
    syncFromStoreRange()
  }
)
</script>

<style scoped>
.insights-calendar-popover {
  position: absolute;
  left: 0;
  top: calc(100% + 0.75rem);
  z-index: 60;
}
</style>

<style scoped>
/* nothing extra */
</style>
